import React, { useState } from 'react';
import * as yup from 'yup';
import { useLocation } from 'react-router-dom';
import { Form, Input, Button, Checkbox, Divider, Typography, message } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { useTranslation } from 'react-i18next';
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import Icon, { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';

import PasswordGenerator from '@Shared/utils/passwordGenerator';
import { useSubmitAuthFormMutation } from '@Entities/signup/api/signupSlice';
import { ga4event } from '@Shared/utils/ga4event';
import { identifySignup, signupEvent } from '@Shared/utils/posthog';
import { emailType } from '@Shared/utils/emailType';
import useYup from '@Shared/utils//useYup';
import useLocalStorage from '@Shared/utils/useLocalStorage';
import { GoogleIcon } from '@Shared/assets/images';

import { FieldHint, RequiredLabel, useFieldPointer, useMinLength } from './fieldHelpers';

import './styles.less';

type AuthFormDataType = {
    firstName: string;
    email: string;
    password: string;
    notificationsAgree?: boolean;
    termsAgree: boolean;
};

type PublicAuthFormDataType = Omit<AuthFormDataType, 'password'>;

// The password goes to the signup API only. localStorage gets this subset,
// and the form restored from storage asks for the password again.
function toPublicAuthData({ firstName, email, termsAgree, notificationsAgree }: AuthFormDataType) {
    return { firstName, email, termsAgree, notificationsAgree };
}

// The form asks no name (Vova 29.10.2026): the server still needs one, so the account is named after its email. The
// server accepts only letters, digits, spaces and ' ` ‘ ’ - @ . ? in a name (UserNamePattern), so john_doe+x@acme.com
// goes as johndoex@acme.com.
export function nameFromEmail(email: string) {
    return email.replace(/[^\p{L}\p{N}\s'`\u2018\u2019\-@.?]/gu, '');
}

const CLIENT_ID = process.env.GOOGLE_CLIENT_ID;
const MIN_PASSWORD = 8;

export default function AuthForm({ handleStep }: { handleStep: (arg1: number) => void }) {
    const storage = useLocalStorage();
    const location = useLocation();
    const { t } = useTranslation('translation', { keyPrefix: 'signup' });
    const [submitAuthForm, { isLoading }] = useSubmitAuthFormMutation();
    const [isOAuthLoading, setIsOAuthLoading] = useState(false);
    const [errors, setErrors] = useState<Partial<AuthFormDataType>>({});
    const [form] = Form.useForm();
    const { pointAt, recheckIfWrong, shakeClass } = useFieldPointer(form);
    const password = useMinLength(MIN_PASSWORD);

    const yupRules = useYup({
        // yup's .email() takes a@b, a domain without a zone; the address needs a dot and a zone of two letters or more.
        email: yup
            .string()
            .required(t('form.error.enter_email'))
            .email(t('form.error.email_incorrect'))
            .matches(/^[^\s@]+@[^\s@]+\.[^\s@.]{2,}$/, t('form.error.email_incorrect')),
    });

    const searchParams = new URLSearchParams(location.search);
    const emailFromQuery = searchParams.get('email') || '';

    const storedAuthData: Partial<PublicAuthFormDataType> = storage.read('authStepData', {});
    const initialValues = {
        email: storedAuthData.email || '',
        notificationsAgree: storedAuthData.notificationsAgree,
        ...(emailFromQuery
            ? {
                  email: emailFromQuery.toLowerCase(),
              }
            : {}),
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement> | CheckboxChangeEvent) => {
        const fieldName = e.target?.id?.replace('auth-form_', '');
        ga4event({
            event_name: `enter_${fieldName}`,
            event_option1: 'advertiser registration funnel',
            event_option2: 'filled name email password',
        });
        // setErrors((prev: Partial<AuthFormDataType>) =>
        //     Object.fromEntries(
        //         Object.entries(prev).filter(([key]: [key: string, value: unknown]) => key !== fieldName),
        //     ),
        // );
    };

    const handleSubmit = async (formData: AuthFormDataType, method: 'email' | 'google' = 'email') => {
        try {
            const res = await submitAuthForm(formData);
            if (res?.error) {
                const errors = (res?.error?.data?.errors as AuthFormDataType) || {};
                ga4event({
                    event_name: 'send_advertiser_signup_lead_success_f',
                    event_option1: 'advertiser registration funnel',
                    event_option2: 'filled name email password',
                    event_option4: `error ${Object.values(errors).join(' ')}`,
                });
                setErrors(errors);
                if (errors.email || errors.firstName) pointAt('email');
                else if (errors.password) pointAt('password');
            } else {
                ga4event({
                    event_name: 'send_advertiser_signup_lead_success_f',
                    event_option1: 'advertiser registration funnel',
                    event_option2: 'filled name email password',
                });

                identifySignup(res?.data?.uuid, formData.email);
                signupEvent('signup:details_submitted', { method, email_type: emailType(formData.email) });

                storage.write('currentStep', 'CompanyStep');
                storage.write('stepUuid', res?.data?.uuid || '');
                storage.write('authStepData', toPublicAuthData(formData));
                handleStep(1);
            }
        } catch (err) {
            console.error('error: ', err);
        }
    };

    const GoogleSignUpButton = () => {
        const googleSignUp = useGoogleLogin({
            onSuccess: async (tokenResponse) => {
                try {
                    const formData = form.getFieldsValue();
                    const userInfo = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
                        headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
                    }).then((res) => res.json());

                    const userData = {
                        email: userInfo.email,
                        firstName: userInfo.given_name || nameFromEmail(userInfo.email),
                        password: PasswordGenerator(16),
                        termsAgree: true,
                        notificationsAgree: formData.notificationsAgree || false,
                    };
                    await handleSubmit(userData, 'google');
                } catch (error) {
                    console.error(error);
                } finally {
                    setIsOAuthLoading(false);
                }
            },
            onError: () => message.error(t('auth_form.google_oauth_error')),
        });

        return (
            <Button
                block
                htmlType="button"
                type="default"
                icon={
                    <Icon
                        component={() => (
                            <img
                                src={GoogleIcon}
                                alt="credit-card"
                            />
                        )}
                    />
                }
                // A quiet grey button: the email form above is the main way in (PandaDoc's layout).
                style={{
                    fontWeight: 600,
                    color: '#052d57',
                    background: '#F0F2F5',
                    border: 'none',
                    boxShadow: 'none',
                }}
                size="large"
                loading={isOAuthLoading}
                onClick={() => {
                    ga4event({
                        event_name: 'click_sign_up_via',
                        event_option1: 'advertiser registration funnel',
                        event_option2: 'filled subdomain',
                        event_option4: 'google',
                    });

                    googleSignUp();
                    setIsOAuthLoading(true);
                }}
            >
                {t('auth_form.signup_with_google')}
            </Button>
        );
    };

    return (
        <>
            <Form
                initialValues={initialValues}
                name="auth-form"
                layout="vertical"
                form={form}
                onFinish={(values: Pick<AuthFormDataType, 'email' | 'password' | 'notificationsAgree'>) => {
                    if (password.tooShort(values.password)) {
                        password.setWrong(true);
                        pointAt('password');
                        return;
                    }
                    handleSubmit({ ...values, firstName: nameFromEmail(values.email), termsAgree: true });
                }}
                requiredMark={false}
                // The browser's own bubble for type="email" is off: the form shows its errors under the fields.
                noValidate
                validateTrigger="onBlur"
                onFinishFailed={() => {
                    password.onLeave(form.getFieldValue('password'));
                    pointAt('email');
                }}
            >
                <Form.Item
                    label={<RequiredLabel text={t('auth_form.email')} />}
                    name="email"
                    rules={yupRules}
                    validateStatus={errors?.email || errors?.firstName ? 'error' : undefined}
                    help={errors?.email || errors?.firstName || undefined}
                    className={shakeClass('email')}
                >
                    <Input
                        size="large"
                        type="email"
                        name="email"
                        autoComplete="email"
                        onBlur={handleChange}
                        onChange={recheckIfWrong('email')}
                        className="signup-input"
                    />
                </Form.Item>
                {/* The inner item keeps the value without a message area of its own; the hint is our FieldHint. */}
                <Form.Item
                    label={<RequiredLabel text={t('auth_form.password')} />}
                    htmlFor="auth-form_password"
                    className={shakeClass('password')}
                >
                    <Form.Item
                        name="password"
                        noStyle
                    >
                        <Input.Password
                            size="large"
                            // A new account: the browser offers to save it and to suggest a strong password.
                            name="password"
                            autoComplete="new-password"
                            onBlur={(e) => {
                                handleChange(e);
                                password.onLeave(e.target.value);
                            }}
                            onChange={(e) => password.onType(e.target.value)}
                            // The red border; the input's own status wins over the one antd's form item gives it.
                            status={password.wrong || errors?.password ? 'error' : undefined}
                            className="signup-input"
                            // The eye shows what a click does: an open eye reveals the password, a crossed one hides.
                            iconRender={(visible) => (visible ? <EyeInvisibleOutlined /> : <EyeOutlined />)}
                        />
                    </Form.Item>
                    <FieldHint wrong={password.wrong || Boolean(errors?.password)}>
                        {errors?.password || t('form.hint.password_short')}
                    </FieldHint>
                </Form.Item>
                <Form.Item
                    className="item-custom-checkbox"
                    rootClassName="item-custom-root-checkbox"
                    name="notificationsAgree"
                    validateStatus={errors?.notificationsAgree ? 'error' : ''}
                    help={errors?.notificationsAgree || null}
                    valuePropName="checked"
                >
                    <Checkbox
                        className="checkbox-custom"
                        onChange={handleChange}
                    >
                        {t('auth_form.agree_notiication')}
                    </Checkbox>
                </Form.Item>
                <Form.Item style={{ marginBottom: 12 }}>
                    <Button
                        block
                        htmlType="submit"
                        type="primary"
                        style={{ fontWeight: 600 }}
                        size="large"
                        loading={isLoading && !isOAuthLoading}
                    >
                        {t('auth_form.signup_with_email')}
                    </Button>
                </Form.Item>
                {/* Signing up is accepting the terms: the server still records termsAgree, sent as true. */}
                <Typography.Text
                    type="secondary"
                    style={{ display: 'block', fontSize: 13 }}
                >
                    {t('auth_form.by_signing_up')}{' '}
                    <a
                        href="https://tapfiliate.com/terms/"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {t('auth_form.terms_conditions')}
                    </a>{' '}
                    {t('auth_form.and')}{' '}
                    <a
                        href="https://tapfiliate.com/privacy/"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {t('auth_form.privacy_policy')}
                    </a>
                </Typography.Text>
                <Divider plain>{t('auth_form.or')}</Divider>
                <GoogleOAuthProvider clientId={CLIENT_ID}>
                    <GoogleSignUpButton />
                </GoogleOAuthProvider>
                <Typography.Paragraph style={{ textAlign: 'center', margin: '24px 0 0', fontWeight: 600 }}>
                    {t('auth_form.have_account')}{' '}
                    <a href="https://app.tapfiliate.com/login/">{t('auth_form.log_in')}</a>
                </Typography.Paragraph>
            </Form>
        </>
    );
}
